import { useEffect, useState } from 'react';
import type { TradeItem } from '@/types';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DecimalInput } from '@/components/ui/DecimalInput';
import { useToastStore } from '@/stores';
import { getErrorMessage } from '@/utils/get-error-message';
// Import canPerform from its specific module, NOT the @/features/trades/hooks barrel
// (the barrel pulls the whole hooks tree, which mocking-sensitive unit tests can't satisfy).
import { canPerform } from '@/features/trades/hooks/trade-actions';
import type { usePartialRecall } from '@/hooks/trades';
import { divHalfUp } from '@/services/trades/trade-calculations';
import { getEffectiveSettlementCurrency, getSettlementDisplay } from '@/utils/trades/settlement-ccy';
import { formatCurrencyExact } from '@/utils/trades/mtm-calculations';

/**
 * The fields the modal needs from a position — a structural subset so BOTH the Trades
 * list (TradeItem) and the Single Trade View (PositionDetail) can pass their row directly.
 *
 * LR-576 (feedback п.71): the margin-price/fx fields for the Last Marked preview are
 * optional and deliberately WIDE (`number | string`) — the blotter passes TradeItem
 * numbers while the STV passes PositionDetail decimal-STRINGS. They are coerced via
 * `toFiniteOrNull` BEFORE the `getSettlementDisplay` cast (LR-461 runtime-type class:
 * a missed coercion would silently render a dash, never trust the TS type here).
 */
type PartialRecallTarget = Pick<
  TradeItem,
  'id' | 'symbol_id' | 'quantity' | 'trade_type' | 'status' | 'remaining_quantity' | 'collateral_type'
> & {
  // Required (kvi review r1 #4): both real callers (TradeItem / PositionDetail)
  // carry it, and an absent code would send '' into Intl → RangeError fallback.
  currency: string;
  settlement_currency?: string | null;
  fx_rate?: number | string | null;
  margin_price?: number | string | null;
  margin_price_converted?: number | string | null;
  marked_margin_price?: number | string | null;
  marked_margin_price_converted?: number | string | null;
  collateral_to_return?: number | string | null;
};

/** Finite number from a JS number OR a backend decimal-string; null otherwise.
 *  `Number.isFinite('163.20')` is false — the parseFloat step is the whole point. */
const toFiniteOrNull = (v: number | string | null | undefined): number | null => {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/** `held × qty / booked` in cents, half-up, as the BO recall leg quantizes it — float math
 *  rounds an exact half-cent down. */
const collateralShare = (held: number, qty: number, booked: number): number =>
  Number(divHalfUp(BigInt(Math.round(held * 100)) * BigInt(Math.round(qty)), BigInt(booked))) / 100;

interface PartialRecallModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The position the recall/return is booked against (null when the dialog is closed). */
  trade: PartialRecallTarget | null;
  /** The `usePartialRecall` mutation handle, owned by the hosting page (single subscription). */
  partialRecall: ReturnType<typeof usePartialRecall>;
}

/**
 * LR-388: book a pending partial recall (lend side) / return (borrow side) against a
 * position. The trader enters how many shares to recall/return (≤ the remaining markable
 * quantity) and a required settlement date (LR-515: a dateless recall can never be settled);
 * on success MTM marks only the net remaining shape.
 */
export function PartialRecallModal({ isOpen, onClose, trade, partialRecall }: PartialRecallModalProps) {
  const [quantity, setQuantity] = useState<number | undefined>(undefined);
  const [settlementDate, setSettlementDate] = useState('');
  const { mutate, isPending, error, reset } = partialRecall;
  const { addToast } = useToastStore();

  // Reset inputs + any prior error each time the dialog opens for a (new) trade.
  useEffect(() => {
    if (isOpen) {
      setQuantity(undefined);
      setSettlementDate('');
      reset();
    }
  }, [isOpen, trade?.id, reset]);

  if (!trade) return null;

  const isReturn = trade.trade_type === 'borrow';
  const noun = isReturn ? 'Return' : 'Recall';
  const booked = trade.quantity;
  const remaining = trade.remaining_quantity ?? trade.quantity;

  // LR-576 (feedback п.71): resolve the margin-price display pair ONCE through the
  // settlement-ccy doctrine module (settlement-primary, like the blotter columns —
  // no local twin fallbacks, settlement-ccy.ts:16 forbids them). The minimal basis
  // object is cast to TradeItem only AFTER every money/fx field went through
  // toFiniteOrNull. Fallback for a never-marked position: the BOOKING figures under
  // an honest Trade-Margin label (the customer's own prescription in п.72) — never
  // booking numbers under a "Last Marked" label.
  const priceBasis = {
    currency: trade.currency ?? '',
    settlement_currency: trade.settlement_currency ?? undefined,
    fx_rate: toFiniteOrNull(trade.fx_rate),
    margin_price: toFiniteOrNull(trade.margin_price),
    margin_price_converted: toFiniteOrNull(trade.margin_price_converted),
    marked_margin_price: toFiniteOrNull(trade.marked_margin_price),
    marked_margin_price_converted: toFiniteOrNull(trade.marked_margin_price_converted),
  } as unknown as TradeItem;
  const markedPrice = getSettlementDisplay(priceBasis, 'marked_margin_price');
  const bookingPrice = markedPrice == null ? getSettlementDisplay(priceBasis, 'margin_price') : null;
  const marginPrice = markedPrice ?? bookingPrice;
  // Booking label ONLY when the booking figure actually renders; with no data at
  // all the row keeps the Last-Marked label with a dash (mirrors the LR-522
  // blank-until-marked grid columns).
  const useBookingLabel = markedPrice == null && bookingPrice != null;
  const priceLabel = useBookingLabel ? 'Trade Margin Price' : 'Last Marked Margin Price';
  const valueLabel = useBookingLabel ? 'Trade Margin Value' : 'Last Marked Margin Value';
  // What BO actually gives back: the recalled share of the cash the trade holds there, counting
  // the MTM transfers still on their way (Settle waits for them). Differs from the Last Marked
  // value where BO is not at the mark (rebate, no pool, a cross-currency trade converted at the
  // MTM date's COB rather than the booked fx_rate).
  const toReturn = toFiniteOrNull(trade.collateral_to_return);
  const collateralCcy = getEffectiveSettlementCurrency(trade);
  const overRemaining = quantity != null && quantity > remaining;
  const quantityInvalid = quantity == null || quantity < 1 || overRemaining;
  // LR-515: settlement date is required at booking — a recall booked without one
  // can never be settled (backend settle guard rejects it), so block the CTA until set.
  // The remaining-after preview still keys off quantity alone (quantityInvalid).
  const invalid = quantityInvalid || !settlementDate;

  const handleConfirm = () => {
    // Guard the full path (mirrors NoticeModal): re-check validity + bounds before firing,
    // so a stale row / keyboard / race cannot submit an out-of-range or ineligible recall.
    if (!canPerform(trade, 'partial_recall') || invalid) return;
    mutate(
      {
        id: trade.id,
        quantity: quantity as number,
        // Guaranteed non-empty: `invalid` blocks the CTA + handleConfirm re-guards on !settlementDate.
        settlement_date: settlementDate,
      },
      {
        onSuccess: () => {
          addToast('success', `Partial ${noun.toLowerCase()} of ${(quantity as number).toLocaleString('en-US')} booked`);
          onClose();
        },
        onError: (e) => addToast('error', getErrorMessage(e, `Failed to book partial ${noun.toLowerCase()}`)),
      },
    );
  };

  return (
    <ConfirmDialog
      show={isOpen}
      onClose={onClose}
      title={`Partial ${noun}`}
      confirmLabel={`Book ${noun}`}
      isLoading={isPending}
      onConfirm={handleConfirm}
      // No enabled-but-silent Book: an empty/out-of-range quantity visibly disables
      // the CTA, matching the sibling AmendModal (LR-478 review r4). handleConfirm's
      // guard stays as the race/stale backstop.
      confirmDisabled={invalid}
      error={error}
      errorFallback={`Failed to book partial ${noun.toLowerCase()}`}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {isReturn ? 'Return' : 'Recall'} part of position <strong>#{trade.id}</strong>{' '}
          ({trade.symbol_id}). MTM will then mark only the remaining shape.
        </p>

        <div>
          <label htmlFor="recall-qty" className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
            Quantity to {noun.toLowerCase()}
          </label>
          <DecimalInput
            id="recall-qty"
            value={quantity}
            onChange={setQuantity}
            precision={0}
            minValue={1}
            placeholder="0"
            // LR-478 review r3/r7: announce + associate the over-limit error AND
            // always describe the input with the booked/remaining-markable constraint,
            // mirroring the sibling AmendModal (which always wires its preview id).
            aria-invalid={overRemaining}
            aria-describedby={overRemaining ? 'recall-qty-help recall-qty-error' : 'recall-qty-help'}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono tabular-nums focus:border-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          />
          <p id="recall-qty-help" className="mt-1 text-[11px] text-gray-500">
            Booked {booked.toLocaleString('en-US')} · remaining markable{' '}
            <span className="font-medium text-gray-700">{remaining.toLocaleString('en-US')}</span>
          </p>
          {overRemaining && (
            <p id="recall-qty-error" role="alert" className="mt-1 text-[11px] text-red-600">
              Cannot exceed the remaining markable quantity ({remaining.toLocaleString('en-US')}).
            </p>
          )}
          {/* LR-478 S3: live after-state preview — what the position's remaining
              markable quantity becomes once this recall/return books. Integer math
              on JS-number quantities (HON-03); rendered only for a valid in-range
              qty so it never shows a negative or NaN. */}
          {quantity != null && Number.isFinite(quantity) && !quantityInvalid && (
            <p className="mt-1 text-[11px] text-gray-600" aria-live="polite">
              Remaining after {noun.toLowerCase()}:{' '}
              <span className="font-semibold tabular-nums text-gray-900">
                {(remaining - quantity).toLocaleString('en-US')}
              </span>
              <span className="text-gray-500"> of {booked.toLocaleString('en-US')} booked</span>
            </p>
          )}
        </div>

        {/* LR-576 (feedback п.71): the position's Last Marked Margin Price (booking
            Trade-Margin fallback when never marked) and its value on the RECALLED
            quantity — decision support for the BO shares/collateral exchange. The
            value row mirrors the remaining-after gate above so it never renders
            for an empty/out-of-range quantity. Both figures share one resolved
            {value, ccy} pair, so price × qty stays internally consistent even on
            the degenerate-fx fallback. */}
        <div className="rounded-md bg-gray-50 px-3 py-2">
          <p className="flex items-center justify-between text-[11px] text-gray-600">
            <span>{priceLabel}</span>
            <span
              className="font-semibold tabular-nums text-gray-900"
              // Full-precision resolved price on hover: marks carry >2dp, the cell
              // rounds to 2dp while the value row multiplies the FULL price — the
              // title lets a trader reconcile price × qty without the 2dp rounding
              // reading as a mismatch (review r1 #4).
              title={
                marginPrice
                  ? `${parseFloat(marginPrice.value.toPrecision(12))} ${marginPrice.ccy}`
                  : undefined
              }
            >
              {marginPrice ? formatCurrencyExact(marginPrice.value, marginPrice.ccy) : '—'}
            </span>
          </p>
          {quantity != null && Number.isFinite(quantity) && !quantityInvalid && (
            <p className="mt-1 flex items-center justify-between text-[11px] text-gray-600" aria-live="polite">
              <span>
                {valueLabel} <span className="text-gray-400">· {noun.toLowerCase()} qty</span>
              </span>
              <span className="font-semibold tabular-nums text-gray-900">
                {marginPrice ? formatCurrencyExact(marginPrice.value * quantity, marginPrice.ccy) : '—'}
              </span>
            </p>
          )}
          {/* A cash figure: non-cash collateral goes back to BO as the collateral securities. */}
          {trade.collateral_type !== 'uncollateralized' && trade.collateral_type !== 'non_cash' &&
            booked > 0 && quantity != null && Number.isFinite(quantity) && !quantityInvalid && (
            <p className="mt-1 flex items-center justify-between text-[11px] text-gray-600" aria-live="polite">
              <span>
                Collateral returned <span className="text-gray-400">· {noun.toLowerCase()} qty</span>
              </span>
              <span
                className="font-semibold tabular-nums text-gray-900"
                title={
                  toReturn == null
                    ? "Not known: an FX rate is missing, or the trade's BackOffice cash does not add up"
                    : undefined
                }
              >
                {toReturn != null
                  ? formatCurrencyExact(collateralShare(toReturn, quantity, booked), collateralCcy)
                  : '—'}
              </span>
            </p>
          )}
        </div>

        <div>
          <label htmlFor="recall-settlement-date" className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
            Settlement date <span className="normal-case text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="recall-settlement-date"
            type="date"
            required
            value={settlementDate}
            onChange={(e) => setSettlementDate(e.target.value)}
            aria-required="true"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          />
          <p className="mt-1 text-[11px] text-gray-500">
            Contractual date the {noun.toLowerCase()} is expected to settle. Required to book —
            it can be changed later via "Edit date" on the trade's Recalls tab. Settling stays
            a manual click there.
          </p>
        </div>
      </div>
    </ConfirmDialog>
  );
}
